import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface DisasterTip {
  id: number;
  title: string;
  description: string;
  icon: string;
  priority: 'critical' | 'important' | 'recommended';
}

interface DisasterPreparednessProps {
  tips: DisasterTip[];
}

const DisasterPreparedness: React.FC<DisasterPreparednessProps> = ({ tips }) => {
  const priorityStyles = {
    critical: 'border-error bg-error/5',
    important: 'border-warning bg-warning/5',
    recommended: 'border-primary bg-primary/5'
  };

  const priorityIcons = {
    critical: 'text-error',
    important: 'text-warning',
    recommended: 'text-primary'
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-2xl font-headline font-bold text-foreground mb-2 flex items-center space-x-2">
          <Icon name="HomeModernIcon" size={28} variant="solid" className="text-primary" />
          <span>Disaster Preparedness Guide</span>
        </h3>
        <p className="text-muted-foreground">
          Essential steps to protect your pets during natural disasters and emergencies
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {tips.map((tip) => (
          <div
            key={tip.id}
            className={`border-2 rounded-lg p-5 transition-all duration-300 hover:shadow-md ${priorityStyles[tip.priority]}`}
          >
            <div className="flex items-start space-x-4">
              <div className={`flex-shrink-0 ${priorityIcons[tip.priority]}`}>
                <Icon name={tip.icon as any} size={32} variant="solid" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-headline font-semibold text-foreground mb-2">{tip.title}</h4>
                <p className="text-muted-foreground">{tip.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-primary/10 border-2 border-primary rounded-lg p-6">
        <h4 className="text-lg font-headline font-semibold text-primary mb-4 flex items-center space-x-2">
          <Icon name="DocumentTextIcon" size={24} variant="solid" />
          <span>Emergency Contact Card</span>
        </h4>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-semibold text-foreground mb-2">Keep These Numbers Accessible:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Your veterinarian</li>
              <li>• Emergency vet clinic</li>
              <li>• Poison control hotline</li>
              <li>• Local animal shelter</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-foreground mb-2">Pet Information to Have Ready:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Medical records</li>
              <li>• Vaccination history</li>
              <li>• Microchip number</li>
              <li>• Recent photos</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisasterPreparedness;